import { useMemo } from "react";
import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { useAgeingBands } from "./useAgeingBands";
import { mapCustomerAgeingApiToUi } from "@/lib/mappers/customer-detail";
import { mockInvoicesByCustomerId } from "@/lib/demo-data/customer-transactions";
import { isStubResponse } from "@/types/api/customer-detail";
import type { CustomerAgeingApi, StubResponse } from "@/types/api/customer-detail";
import type { AgeingBandConfig } from "@/types/ageing-analysis";
import type { CustomerAgeingUI, CustomerAgeingBandUI, InvoiceUI } from "@/types/ui/customer-detail";

function calculateDemoAgeing(invoices: InvoiceUI[], bands: AgeingBandConfig[]): CustomerAgeingUI {
  const today = new Date();
  const bandTotals = new Map<number, number>();

  for (const band of bands) {
    bandTotals.set(band.sequence, 0);
  }

  for (const invoice of invoices) {
    const dueDate = new Date(invoice.dueDate);
    const daysOld = Math.floor(
      (today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    for (const band of bands) {
      if (daysOld >= band.startDays && (band.endDays === null || daysOld <= band.endDays)) {
        bandTotals.set(band.sequence, (bandTotals.get(band.sequence) ?? 0) + invoice.amount);
        break;
      }
    }
  }

  const uiBands: CustomerAgeingBandUI[] = bands.map((x) => ({
    sequence: x.sequence,
    label: x.label,
    bandLabel: x.bandLabel,
    startDays: x.startDays,
    endDays: x.endDays,
    totalOutstanding: bandTotals.get(x.sequence) ?? 0,
    color: x.color,
  }));

  const total = uiBands.reduce((acc, x) => acc + x.totalOutstanding, 0);
  const overdue = uiBands.filter((x) => x.startDays > 0).reduce((acc, x) => acc + x.totalOutstanding, 0);

  return { bands: uiBands, total, overdue };
}

export function useCustomerAgeing(customerId: string | undefined, currency?: string, isConsolidatedView?: boolean) {
  const { apiGet } = useApi();
  const { data: ageingBands } = useAgeingBands();

  const demoInvoices = customerId
    ? isConsolidatedView
      ? Object.values(mockInvoicesByCustomerId).flat()
      : mockInvoicesByCustomerId[customerId] ?? []
    : [];

  const demoAgeing = useMemo(
    () => (ageingBands ? calculateDemoAgeing(demoInvoices, ageingBands) : null),
    [demoInvoices, ageingBands],
  );

  return useModeAwareQuery<CustomerAgeingUI | null, Error>(
    ["customer-ageing", customerId, currency, isConsolidatedView],
    async ({ signal }) => {
      if (!customerId || !ageingBands) return null;

      try {
        const queryParams: Record<string, string | boolean> = {};
        if (currency) queryParams.currency = currency;
        if (isConsolidatedView) queryParams.consolidated = true;

        const qs = Object.entries(queryParams)
          .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
          .join('&');
        const url = `/api/v1/customers/${customerId}/aging${qs ? `?${qs}` : ''}`;

        const response = await apiGet<CustomerAgeingApi | StubResponse>(
          url,
          undefined,
          signal
        );

        if (isStubResponse(response)) {
          return null;
        }

        return mapCustomerAgeingApiToUi(response, ageingBands);
      } catch (error) {
        console.error("Error fetching customer ageing:", error);
        return null;
      }
    },
    customerId ? demoAgeing : null,
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}
